<template>
  <div class="acc-bonus-programs">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" />

    <div class="summary">
      <div class="summary-card programs">
        <div class="summary-main">
          <span class="summary-label">Total Programs:</span>
          <span class="summary-value">{{ agg.totalPrograms ?? 0 }}</span>
        </div>
        <div class="summary-status">
          <div class="status-item">
            <span>Active:</span>
            <strong>{{ agg.activeCount ?? 0 }}</strong>
          </div>
          <div class="status-item">
            <span>Completed:</span>
            <strong>{{ agg.completedCount ?? 0 }}</strong>
          </div>
          <div class="status-item">
            <span>Cancelled:</span>
            <strong>{{ agg.cancelledCount ?? 0 }}</strong>
          </div>
          <div class="status-item">
            <span>Incoming:</span>
            <strong>{{ agg.incomingCount ?? 0 }}</strong>
          </div>
        </div>
      </div>

      <div class="summary-card finance">
        <div class="summary-row">
          <span class="summary-label">Total Sales:</span>
          <span class="summary-value">$ {{ formatAmount(agg.totalSales) }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Total Bonus:</span>
          <span class="summary-value">$ {{ formatAmount(agg.totalBonus) }}</span>
        </div>
      </div>

      <div class="summary-card entities">
        <div class="summary-row">
          <span class="summary-label">Brands:</span>
          <span class="summary-value">{{ agg.brandCount ?? 0 }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Publishers:</span>
          <span class="summary-value">{{ agg.publisherCount ?? 0 }}</span>
        </div>
      </div>
    </div>

    <div class="filters">
      <div class="search">
        <div class="option">
          <span>Title</span>
            <artmisads-input
            v-model:value="filters.title"
                 style="width: 180px;height: 32px;"
           
            placeholder="Search..."
            @pressEnter="onSearch"
          />
        </div>
        <div class="option">
          <span>Brand</span>
          <AdminBrandSelect
            v-model="filters.brandId"
            platform="operator"
            width="180px"
            placeholder="All"
            @change="onSearch"
          />
        </div>
        <div class="option">
          <span>Publisher</span>
          <AdminPublisherSelect
            v-model="filters.publisherId"
            width="180px"
            placeholder="All"
            @change="onSearch"
          />
        </div>
        <div class="option">
          <span>CreateDate</span>
          <a-range-picker
            v-model:value="createDateRange"
            style="width: 260px"
            value-format="YYYY-MM-DD"
            @change="onSearch"
          />
        </div>
        <div class="option" ref="statusRef">
          <span>Status</span>
          <artmisads-select
            v-model:value="filters.status"
            style="width: 160px"
            :options="statusOptions"
            :getPopupContainer="() => statusRef"
            allow-clear
            placeholder="All"
            @change="onSearch"
          />
        </div>
        <div class="option" ref="sortRef">
          <span>Sort By</span>
          <artmisads-select
            v-model:value="sortBy"
            style="width: 180px"
            :getPopupContainer="() => sortRef"
            @change="onSortChange"
          >
            <a-select-option value="createDateAsc">
              <Icon name="paixu1" class="iconAsc paixuIcon" />
              <span class="paixuText">CreateDate</span>
            </a-select-option>
            <a-select-option value="createDateDesc" class="paixuIcon">
              <Icon name="paixu1" />
              <span class="paixuText">CreateDate</span>
            </a-select-option>
            <a-select-option value="salesAsc">
              <Icon name="paixu1" class="iconAsc paixuIcon" />
              <span class="paixuText">Sales</span>
            </a-select-option>
            <a-select-option value="salesDesc" class="paixuIcon">
              <Icon name="paixu1" />
              <span class="paixuText">Sales</span>
            </a-select-option>
            <a-select-option value="bonusAsc">
              <Icon name="paixu1" class="iconAsc paixuIcon" />
              <span class="paixuText">Bonus</span>
            </a-select-option>
            <a-select-option value="bonusDesc" class="paixuIcon">
              <Icon name="paixu1" />
              <span class="paixuText">Bonus</span>
            </a-select-option>
          </artmisads-select>
        </div>
      </div>
    
    </div>
    <div class="button">
        <artmisads-button isSquare="true" type="primary" :loading="load" @click="onSearch">
          Search
        </artmisads-button>
        <artmisads-button isSquare="true" @click="onReset">Reset</artmisads-button>
    </div>
    <div class="table-data">
      <artmisads-table
        v-model:pagination="pagination"
        :columns="columns"
        :data-source="dataSource"
        :loading="load"
        :change="onTableChange"
        :scroll="{ x: 1400 }"
        row-key="programId"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'createDate'">
            {{ formatCreateDate(record) }}
          </template>
          <template v-if="column.key === 'salesAmount' || column.key === 'bonusAmount'">
            {{ currencySymbol(record) }} {{ formatAmount(record[column.key]) }}
          </template>
          <template v-if="column.key === 'period'">
            {{ formatPeriod(record) }}
          </template>
          <template v-if="column.key === 'publishers'">
            <a href="javascript:void(0)" class="link" @click="openPublishers(record)">
              {{ record.publisherCount ?? 0 }}
            </a>
          </template>
          <template v-if="column.key === 'status'">
            <Status :type="statusType(record.status)" :text="statusLabel(record.status)" :noBorder="true" />
          </template>
         
        </template>
      </artmisads-table>
    </div>

    <PublishersModal ref="publishersModalRef" />
  </div>
</template>

<script setup>
import { onMounted, reactive, ref,computed } from 'vue'
import moment from 'moment'
import breadcrumbs from '../../breadcrumbs.vue'
import Status from '~/components/Status.vue'
import AdminBrandSelect from '~/components/AdminBrandSelect.vue'
import AdminPublisherSelect from '~/components/AdminPublisherSelect.vue'
import PublishersModal from './publishersModal.vue'
import { accBonusProgramsApi } from '~/server/operator/accBonusPrograms'
import { CURRENCY_DATA_MAPPER,COUNTRY_TIME_TZ } from '~/contants/common'
import { useUserInfoStore } from '@/storeData'
import Icon from '@/components/Icon.vue'
const user = useUserInfoStore()
const statusRef = ref(null)
const sortRef = ref(null)
const publishersModalRef = ref(null)
const load = ref(false)
const dataSource = ref([])
const createDateRange = ref([])
const sortBy = ref('createDateDesc')
const countryCode = user?.userInfo?.countryCode || 'US'
const timeZone = COUNTRY_TIME_TZ[countryCode]
const agg = ref({
  totalPrograms: 0,
  activeCount: 0,
  completedCount: 0,
  cancelledCount: 0,
  incomingCount: 0,
  totalSales: 0,
  totalBonus: 0,
  brandCount: 0,
  publisherCount: 0,
})

const filters = reactive({
  title: '',
  brandId: undefined,
  publisherId: undefined,
  status: null,
})

const sortMap = {
  createDateAsc: { sortField: 'createDate', sortDirection: 'asc' },
  createDateDesc: { sortField: 'createDate', sortDirection: 'desc' },
  salesAsc: { sortField: 'sales', sortDirection: 'asc' },
  salesDesc: { sortField: 'sales', sortDirection: 'desc' },
  bonusAsc: { sortField: 'bonus', sortDirection: 'asc' },
  bonusDesc: { sortField: 'bonus', sortDirection: 'desc' },
}

const pagination = reactive({
  total: 0,
  current: 1,
  size: 10,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
})

const breadcrumbsData = {
  title: 'ACC Bonus Programs',
  des: '',
  links: [
    { name: 'Home', path: '/operator/users' },
    { name: 'Operation', path: '/operator/operation/manualPayments' },
    { name: 'ACC Bonus Programs', path: '/operator/operation/accBonusPrograms' },
  ],
}

const statusOptions = [
  { label: 'All', value: null },
  { label: 'Incoming', value: 'INCOMING' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Cancelled', value: 'CANCELLED' },
]

const columns = [
  { title: 'CreateDate', dataIndex: 'createDate', key: 'createDate', width: 120 },
  { title: 'Title', dataIndex: 'title', key: 'title', width: 160 },
  { title: 'Brand', dataIndex: 'brandName', key: 'brandName', width: 120 },
  { title: 'Sales', dataIndex: 'salesAmount', key: 'salesAmount', width: 120 },
  { title: 'Bonus', dataIndex: 'bonusAmount', key: 'bonusAmount', width: 120 },
  { title: 'Period', dataIndex: 'period', key: 'period', width: 200 },
  { title: 'Publishers', dataIndex: 'publisherCount', key: 'publishers', width: 110 },
  { title: 'Status', dataIndex: 'status', key: 'status', width: 120 },
  // { title: 'Action', dataIndex: 'action', key: 'action', width: 100, fixed: 'right' },
]

const currencySymbol = (record) => CURRENCY_DATA_MAPPER[record.currency] || '$'
const formatAmount = (val) => Number(val || 0).toFixed(2)

const formatCreateDate = (record) => {
  const value = record.createDateInEpochMillis 
  if (!value) return '--'
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    return moment(Number(value)).tz(timeZone).format('YYYY-MM-DD')
  }
  return moment(value).tz(timeZone).format('YYYY-MM-DD')
}

const formatPeriodDate = (d) => (d ? moment(d).tz(timeZone).format('YYYY.MM.DD') : '')
const formatPeriod = (record) => {
  if (record.period) return record.period
  const start = formatPeriodDate(record.startDateInEpochMillis || record.startDate)
  const end = formatPeriodDate(record.endDateInEpochMillis || record.endDate)
  if (!start && !end) return '--'
  return `${start} - ${end}`
}

const statusLabel = (status) => {
  const map = {
    INCOMING: 'Incoming',
    ACTIVE: 'Active',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  }
  return map[status] || status || '--'
}

const statusType = (status) => {
  if (status === 'ACTIVE') return 'active'
  if (status === 'COMPLETED') return 'finished'
  if (status === 'CANCELLED') return 'inactive'
  if (status === 'INCOMING') return 'delivering'
  return 'inactive'
}

const buildQuery = () => {
  const [startCreateDate, endCreateDate] = createDateRange.value || []
  const query = {
    page: pagination.current,
    size: pagination.size,
    title: filters.title || undefined,
    brandId: filters.brandId || undefined,
    publisherId: filters.publisherId || undefined,
    status: filters.status || undefined,
    startCreateDateInEpochMillis: startCreateDate
      ? moment(startCreateDate).tz(timeZone).startOf('day').valueOf()
      : undefined,
    endCreateDateInEpochMillis: endCreateDate
      ? moment(endCreateDate).tz(timeZone).endOf('day').valueOf()
      : undefined,
  }

  if (sortBy.value && sortMap[sortBy.value]) {
    query.sortField = sortMap[sortBy.value].sortField
    query.sortDirection = sortMap[sortBy.value].sortDirection
  }

  return query
}

const getAgg = async () => {
  try {
    const res = await accBonusProgramsApi.getAgg({})
    const data = res?.data ?? res
    if (data) {
      agg.value = {
        totalPrograms: data.totalPrograms ?? data.total ?? 0,
        activeCount: data.activeCount ?? data.active ?? 0,
        completedCount: data.completedCount ?? data.completed ?? 0,
        cancelledCount: data.cancelledCount ?? data.cancelled ?? 0,
        incomingCount: data.incomingCount ?? data.incoming ?? 0,
        totalSales: data.totalSales ?? 0,
        totalBonus: data.totalBonus ?? 0,
        brandCount: data.brandCount ?? data.brands ?? 0,
        publisherCount: data.publisherCount ?? data.publishers ?? 0,
      }
    }
  } catch {
    // ignore agg failure
  }
}

const getList = async () => {
  load.value = true
  try {
    const res = await accBonusProgramsApi.getList(buildQuery())
    const payload = res

    dataSource.value = payload?.data ?? []
    pagination.total = payload?.total 
  } catch {
    dataSource.value = []
    pagination.total = 0
  } finally {
    load.value = false
  }
}

const onSearch = () => {
  pagination.current = 1
  getList()
}

const onSortChange = () => {
  pagination.current = 1
  getList()
}

const onReset = () => {
  filters.title = ''
  filters.brandId = undefined
  filters.publisherId = undefined
  filters.status = null
  createDateRange.value = []
  sortBy.value = 'createDateDesc'
  pagination.current = 1
  getList()
}

const onTableChange = ({ current, pageSize }) => {
  pagination.current = current
  pagination.size = pageSize
  pagination.pageSize = pageSize
  getList()
}

const openPublishers = (record) => {
  publishersModalRef.value?.show(record)
}

onMounted(() => {
  getAgg()
  getList()
})
</script>

<style lang="less" scoped>
.acc-bonus-programs {
  padding: 16px;
  padding-top: unset;
  width: 100%;
}

.summary {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  .summary-card {
    background: #fff;
    border: 1px solid var(--border-4, #ebe9f0);
    border-radius: 12px;
    padding: 16px 20px;
    min-height: 88px;
  }

  .programs {
    // display: flex
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    min-width: 420px;
    flex: 1.4;
  }

  .finance,
  .entities {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    min-width: 200px;
    flex: 1;
  }
  .summary-main{
    margin: 6px 0;
  }
  .summary-main,
  .summary-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .summary-label {
    color: var(--dark-2);
    font-size: 14px;
    font-weight: 500;
  }

  .summary-value {
    color: var(--dark-1);
    font-size: 16px;
    font-weight: 600;
  }

  .summary-status {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 24px;
  }

  .status-item {
    display: flex;
    gap: 6px;
    color: var(--dark-2);
    font-size: 13px;

    strong {
      color: var(--dark-1);
      font-weight: 600;
    }
  }
}

.filters {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;

  .search {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 20px;

    .option {
      display: flex;
      align-items: center;

      > span {
        margin-right: 8px;
        color: var(--dark-2);
        white-space: nowrap;
      }
    }
  }

 
}
.button {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
    margin: 16px 0;
  }

.iconAsc {
  transform: rotate(180deg);
}

.paixuText {
  margin-left: 4px;
}

.table-data {
  border-radius: 12px;
  background-color: var(--color-white);
  padding: 16px;
  box-shadow: var(--section-box-shadow-6);

  :deep(.ant-table-thead > tr > th) {
    background: #f5f5f7;
  }

  .link {
    color: var(--primary, #7653da);
    font-weight: 600;

    &:hover {
      color: var(--primary-hover, #6345c0);
    }
  }

  .action-links {
    white-space: nowrap;
  }
}
</style>
